import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { API } from '../config';
import '../styles/theme.css';
import './Pricing.css';

// ── Tier definitions ──────────────────────────────────────────────────────────

const SEEKER_TIERS = [
  {
    key: 'free', label: 'Free', price: 'R0', period: 'forever',
    features: [
      { text: '5 job matches / month', done: true },
      { text: 'Manual applications', done: true },
      { text: 'Basic application tracking', done: true },
      { text: 'Email notifications', done: true },
      { text: 'AI job matching', done: false },
      { text: '1 auto-application/day (5 days)', done: false },
      { text: 'AI CV optimisation', done: false },
      { text: 'Interview prep tips', done: false },
      { text: 'Salary insights', done: false },
    ],
  },
  {
    key: 'pro', label: 'Pro', price: 'R299', period: 'per month', popular: true,
    features: [
      { text: '50 job matches / month', done: true },
      { text: 'Manual applications', done: true },
      { text: 'Full tracking dashboard', done: true },
      { text: 'Email + SMS alerts', done: true },
      { text: 'AI job matching', done: true },
      { text: '50 auto-applications / month (3/day)', done: true },
      { text: 'Interview prep tips', done: true },
      { text: 'Priority support', done: true },
      { text: 'AI CV optimisation', done: false },
      { text: 'Salary insights', done: false },
    ],
  },
  {
    key: 'premium', label: 'Premium', price: 'R599', period: 'per month',
    features: [
      { text: 'Unlimited job matches', done: true },
      { text: 'Unlimited auto-applications (3/day)', done: true },
      { text: 'AI job matching', done: true },
      { text: 'AI CV optimisation', done: true },
      { text: 'Interview prep tips', done: true },
      { text: 'Salary insights', done: true },
      { text: 'Email + SMS alerts', done: true },
      { text: 'Dedicated support', done: true },
    ],
  },
];

const RECRUITER_TIERS = [
  {
    key: 'starter', label: 'Starter', price: 'R0', period: 'forever',
    features: [
      { text: '1 active job posting', done: true },
      { text: 'View applications', done: true },
      { text: 'Basic filtering', done: true },
      { text: 'Email notifications', done: true },
      { text: 'AI candidate ranking', done: false },
      { text: 'Analytics dashboard', done: false, soon: true },
      { text: 'Featured listings', done: false },
      { text: 'Automated screening', done: false },
      { text: 'Custom branding', done: false, soon: true },
    ],
  },
  {
    key: 'professional', label: 'Professional', price: 'R499', period: 'per month', popular: true,
    features: [
      { text: '5 active job postings', done: true },
      { text: 'AI candidate ranking', done: true },
      { text: '1 featured listing / month', done: true },
      { text: 'Advanced filters & search', done: true },
      { text: 'Analytics dashboard', done: true },
      { text: 'Email templates', done: true },
      { text: 'Priority support', done: true },
      { text: 'Automated screening', done: false },
      { text: 'Custom branding', done: false, soon: true },
    ],
  },
  {
    key: 'business', label: 'Business', price: 'R999', period: 'per month',
    features: [
      { text: '15 active job postings', done: true },
      { text: 'AI candidate recommendations', done: true },
      { text: 'Unlimited featured listings', done: true },
      { text: 'Full analytics suite', done: true },
      { text: 'Automated screening', done: true },
      { text: 'Custom company branding', done: true },
      { text: 'Bulk actions', done: true },
      { text: 'Priority chat support', done: true },
    ],
  },
];

// What the user loses per tier (shown in cancel modal)
const CANCEL_LOSSES = {
  pro:          ['50 job matches/month → drops to 5', 'Auto-apply (50/month)', 'Email + SMS alerts', 'Priority support'],
  premium:      ['Unlimited job matches → drops to 5', 'AI CV optimisation', 'Salary insights', 'Dedicated support'],
  professional: ['5 active job postings → drops to 1', 'AI candidate ranking', 'Featured listings', 'Analytics dashboard'],
  business:     ['15 active job postings → drops to 1', 'Automated screening', 'Custom branding', 'Unlimited featured listings'],
};

const CANCEL_REASONS = [
  'Too expensive',
  'Not using it enough',
  'Found a job / hired someone',
  'Missing features I need',
  'Switching to another platform',
  'Technical issues',
  'Other',
];

// ── Cancel Subscription Modal ─────────────────────────────────────────────────

function CancelModal({ currentTier, userType, onClose, onConfirm, cancelling }) {
  const [reason, setReason] = useState('');
  const [step, setStep] = useState(1);
  const losses = CANCEL_LOSSES[currentTier] || [];
  const freeTier = userType === 'RECRUITER' ? 'Starter' : 'Free';

  return (
    <div className="cancel-modal-overlay" onClick={onClose}>
      <div className="cancel-modal" onClick={e => e.stopPropagation()}>

        {step === 1 && (
          <>
            <div className="cancel-modal-header">
              <div className="cancel-modal-icon">
                <i className="fas fa-heart-broken"></i>
              </div>
              <h2>We're sorry to see you go</h2>
              <p>Before you cancel, here's what you'll lose access to:</p>
            </div>

            {losses.length > 0 && (
              <ul className="cancel-losses-list">
                {losses.map((l, i) => (
                  <li key={i}>
                    <i className="fas fa-times-circle"></i>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="cancel-reason-section">
              <label className="cancel-reason-label">
                Help us improve — why are you cancelling?
              </label>
              <div className="cancel-reason-options">
                {CANCEL_REASONS.map(r => (
                  <button
                    key={r}
                    className={`cancel-reason-btn${reason === r ? ' selected' : ''}`}
                    onClick={() => setReason(r)}
                  >
                    {reason === r && <i className="fas fa-check"></i>}
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="cancel-modal-actions">
              <button className="btn cancel-keep-btn" onClick={onClose}>
                <i className="fas fa-arrow-left"></i> Keep my plan
              </button>
              <button
                className="btn cancel-next-btn"
                onClick={() => setStep(2)}
                disabled={!reason}
              >
                Continue <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="cancel-modal-header">
              <div className="cancel-modal-icon cancel-modal-icon--warn">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h2>Are you sure?</h2>
              <p>
                Your subscription will <strong>not renew</strong> after the current period ends.
                You keep full access to all paid features until then — no features are removed today.
              </p>
            </div>

            <div className="cancel-confirm-box">
              <i className="fas fa-info-circle"></i>
              <span>
                If you were charged recently and believe you're owed a refund, contact us at{' '}
                <a href="mailto:support@joblinkedai.co.za">support@joblinkedai.co.za</a>.
              </span>
            </div>

            <div className="cancel-modal-actions">
              <button className="btn cancel-keep-btn" onClick={() => setStep(1)}>
                <i className="fas fa-arrow-left"></i> Go back
              </button>
              <button
                className="btn cancel-confirm-btn"
                onClick={() => onConfirm(reason)}
                disabled={cancelling}
              >
                {cancelling
                  ? <><i className="fas fa-spinner fa-spin"></i> Cancelling...</>
                  : <><i className="fas fa-times"></i> Yes, cancel my plan</>
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Pricing Card ──────────────────────────────────────────────────────────────

function PricingCard({ tier, currentTier, onUpgrade, onCancel, loading, cancelAtPeriodEnd, subscriptionEndDate }) {
  const isCurrent = tier.key === currentTier;
  const isFree = tier.price === 'R0';
  const isPaidCurrent = isCurrent && !isFree;
  const isCancelling = isPaidCurrent && cancelAtPeriodEnd;

  const formatEndDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className={`pricing-card-new ${tier.popular ? 'featured' : ''} ${isCurrent ? 'current' : ''} ${isCancelling ? 'cancelling' : ''}`}>
      {tier.popular && !isCancelling && <div className="pricing-badge">Most Popular</div>}
      {isCancelling
        ? <div className="pricing-cancelling-badge"><i className="fas fa-clock"></i> Cancels {formatEndDate(subscriptionEndDate)}</div>
        : isCurrent && <div className="pricing-current-badge">Your Plan</div>
      }
      <h3>{tier.label}</h3>
      <div className="pricing-price-new">{tier.price}</div>
      <div className="pricing-period-new">{tier.period}</div>
      <ul className="pricing-features-new">
        {tier.features.map((f, i) => (
          <li key={i} className={f.done ? 'available' : 'unavailable'}>
            <i className={`fas ${f.done ? 'fa-check' : 'fa-times'}`}></i>
            <span>{f.text}</span>
            {f.soon && <span className="coming-soon-badge">Coming Soon</span>}
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <button className="pricing-btn current-btn" disabled>
          <i className="fas fa-check"></i> {isCancelling ? 'Active until expiry' : 'Current Plan'}
        </button>
      ) : isFree ? (
        <button className="pricing-btn free-btn" disabled>Free Forever</button>
      ) : (
        <button
          className="pricing-btn upgrade-btn"
          onClick={() => onUpgrade(tier.key)}
          disabled={loading === tier.key}
        >
          {loading === tier.key
            ? <><i className="fas fa-spinner fa-spin"></i> Redirecting...</>
            : 'Upgrade Now'}
        </button>
      )}

      {isPaidCurrent && !isCancelling && (
        <button className="pricing-cancel-link" onClick={onCancel}>
          Cancel subscription
        </button>
      )}
    </div>
  );
}

// ── Main Pricing Page ─────────────────────────────────────────────────────────

function Pricing() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('user_type');
  const tiers = userType === 'RECRUITER' ? RECRUITER_TIERS : SEEKER_TIERS;

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/payments/tiers/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setCurrentTier(d.current_tier);
          setCancelAtPeriodEnd(d.cancel_at_period_end || false);
          setSubscriptionEndDate(d.subscription_end_date || null);
          localStorage.setItem('user_tier', d.current_tier);
        }
      })
      .catch(() => {});
  }, [token]);

  const handleUpgrade = async (tier) => {
    if (!token) { navigate('/login'); return; }
    setLoading(tier);
    setError('');
    try {
      const res = await fetch(`${API}/payments/create-checkout/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start checkout');
      if (!data.checkout_url) throw new Error('Invalid response from payment provider.');

      // Paystack: simple redirect to hosted checkout page
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message);
      setLoading(null);
    }
  };

  const handleCancelConfirm = async (reason) => {
    setCancelling(true);
    try {
      const res = await fetch(`${API}/payments/cancel-subscription/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) {
        const data = await res.json();
        setCancelAtPeriodEnd(true);
        setSubscriptionEndDate(data.subscription_end_date);
        setShowCancel(false);
        setCancelSuccess(true);
        setTimeout(() => setCancelSuccess(false), 8000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed to cancel. Please try again.');
        setShowCancel(false);
      }
    } catch {
      setError('Network error. Please try again.');
      setShowCancel(false);
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const res = await fetch(`${API}/payments/reactivate-subscription/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCancelAtPeriodEnd(false);
        setCancelSuccess(false);
      }
    } catch {}
    finally { setReactivating(false); }
  };

  const formatEndDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className={`pricing-page ${isDark ? 'dark' : ''}`}>
      <div className="pricing-header">
        <button className="pricing-back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
        <h1>Choose Your Plan</h1>
        <p>{userType === 'RECRUITER' ? 'Scale your hiring with the right tools' : 'Find your next opportunity faster'}</p>
      </div>

      {error && (
        <div className="pricing-error">
          <i className="fas fa-exclamation-circle"></i> {error}
          <button
            onClick={() => setError('')}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {cancelSuccess && (
        <div className="pricing-success">
          <i className="fas fa-check-circle"></i>
          <span>
            Cancellation scheduled. You keep full access until <strong>{formatEndDate(subscriptionEndDate)}</strong>.
          </span>
          <button className="pricing-undo-btn" onClick={handleReactivate} disabled={reactivating}>
            {reactivating ? 'Undoing...' : 'Undo'}
          </button>
        </div>
      )}

      {!cancelSuccess && cancelAtPeriodEnd && subscriptionEndDate && (
        <div className="pricing-warn-banner">
          <i className="fas fa-clock"></i>
          <span>
            Your subscription is set to cancel on <strong>{formatEndDate(subscriptionEndDate)}</strong>. You have full access until then.
          </span>
          <button className="pricing-undo-btn" onClick={handleReactivate} disabled={reactivating}>
            {reactivating ? 'Undoing...' : 'Keep plan'}
          </button>
        </div>
      )}

      <div className="pricing-cards-grid">
        {tiers.map(tier => (
          <PricingCard
            key={tier.key}
            tier={tier}
            currentTier={currentTier}
            onUpgrade={handleUpgrade}
            onCancel={() => setShowCancel(true)}
            loading={loading}
            cancelAtPeriodEnd={cancelAtPeriodEnd}
            subscriptionEndDate={subscriptionEndDate}
          />
        ))}
      </div>

      <div className="pricing-enterprise">
        <h3>Need more? Enterprise plans available</h3>
        <p>Unlimited postings, dedicated support, API access, and custom integrations</p>
        <a href="mailto:enterprise@joblinked.ai" className="pricing-enterprise-btn">
          <i className="fas fa-envelope"></i> Contact Sales
        </a>
      </div>

      {showCancel && (
        <CancelModal
          currentTier={currentTier}
          userType={userType}
          onClose={() => setShowCancel(false)}
          onConfirm={handleCancelConfirm}
          cancelling={cancelling}
        />
      )}
    </div>
  );
}

export default Pricing;
